import { useNavigate, useParams } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import appConfig from '../config/app-config';

const API = appConfig.apis.softwarePortfolioApiEnv;
const API_KEY = API.key;

type Repository = {
    name: string;
    repository: string | null | undefined;
    description: string | null | undefined;
    language: string | null | undefined;
    version: string | null | undefined;
    project_version: string | null | undefined;
    frameworks: Record<string, string>;
    package_manager: string | null | undefined;
    subprojects: any[];
    test_coverage: any;
    dependencies: any[];
    infrastructure: any;
    active: boolean;
    readme: boolean;
    ignored_dirs: string[];
};

export const useSoftwarePortfolio = () => {
    const navigate = useNavigate();
    const showModal = useSignal(false);
    const modalTitle = useSignal('');
    const modalData = useSignal<any>(null);
    const modalType = useSignal<'frameworks' | 'dependencies' | 'ignored_dirs'>('frameworks');
    const expandedProjects = useSignal<Set<string>>(new Set());

    const {
        page = '0',
        pageSize = '200',
        field: fieldSorted = 'name',
        order: orderSorted = 'ASC',
        filter,
    } = useParams();

    let searchTerm = '';
    let activeFilter = '';
    if (filter) {
        [searchTerm = ''] = filter.match(/=(.*?);/g)?.map((m: string) => m.slice(1, -1)) ?? [''];
        const activeMatch = filter.match(/a=(.*?);/);
        if (activeMatch) activeFilter = activeMatch[1];
    }

    const params = new URLSearchParams({
        page,
        pageSize,
        search: searchTerm,
        sortField: fieldSorted,
        sortOrder: orderSorted,
    });
    if (activeFilter) params.set('active', activeFilter);
    const url = `${API.address}/softwares/page-list?${params.toString()}`;

    const getCoveragePercentage = (testCoverage: any): number => {
        if (!testCoverage || !testCoverage.line_rate) return 0;
        const rate = parseFloat(testCoverage.line_rate);
        return Math.round(rate * 100);
    };

    const getGitHubUrl = (repository: string | null | undefined): string | null => {
        if (!repository) return null;

        // Converter git@github.com:user/repo.git para https://github.com/user/repo
        if (repository.startsWith('git@github.com:')) {
            return repository
                .replace('git@github.com:', 'https://github.com/')
                .replace('.git', '');
        }

        // Se já é HTTPS, apenas remover .git se existir
        if (repository.startsWith('https://github.com/')) {
            return repository.replace('.git', '');
        }

        return repository;
    };

    const toggleProject = (projectName: string) => {
        const newExpanded = new Set(expandedProjects.value);
        if (newExpanded.has(projectName)) {
            newExpanded.delete(projectName);
        } else {
            newExpanded.add(projectName);
        }
        expandedProjects.value = newExpanded;
    };

    const openModal = (title: string, data: any, type: 'frameworks' | 'dependencies' | 'ignored_dirs') => {
        modalTitle.value = title;
        modalData.value = data;
        modalType.value = type;
        showModal.value = true;
    };

    const pageChanged = (pageNumber: number) => {
        pageNumber = pageNumber - 1;
        navigate(urlNavigate(pageNumber, Number(pageSize), fieldSorted, orderSorted, searchTerm, activeFilter), { replace: true });
    };

    const pageSizeChanged = (size: number) => {
        navigate(urlNavigate(0, size, fieldSorted, orderSorted, searchTerm, activeFilter), { replace: true });
    };

    const pageSorted = (field: string, order: 'ASC' | 'DESC') => {
        navigate(urlNavigate(0, Number(pageSize), field, order, searchTerm, activeFilter), { replace: true });
    };

    const searchChanged = (value: string) => {
        navigate(urlNavigate(0, Number(pageSize), fieldSorted, orderSorted, value, activeFilter), { replace: true });
    };

    const activeFilterChanged = (value: string) => {
        navigate(urlNavigate(0, Number(pageSize), fieldSorted, orderSorted, searchTerm, value), { replace: true });
    };

    const onFilterClean = () => {
        navigate(urlNavigate(0, Number(pageSize), fieldSorted, orderSorted, '', ''), { replace: true });
    };

    const urlNavigate = (pageNumber: number, size: number, field: string, order: string, search: string, active: string = '') => {
        const activePart = active ? `a=${active};` : '';
        return `/ti/software-portfolio/${pageNumber}/${size}/${field}/${order}/d=${search};${activePart}`;
    };

    return {
        // State
        showModal,
        modalTitle,
        modalData,
        modalType,
        expandedProjects,

        // Params
        page,
        pageSize,
        fieldSorted,
        orderSorted,
        searchTerm,
        activeFilter,
        url,
        API_KEY,

        // Functions
        getCoveragePercentage,
        getGitHubUrl,
        toggleProject,
        openModal,
        pageChanged,
        pageSizeChanged,
        pageSorted,
        searchChanged,
        activeFilterChanged,
        onFilterClean,

        // Types
        Repository: {} as Repository
    };
};
